import { registerSW } from 'virtual:pwa-register';

/**
 * Register the service worker generated by vite-plugin-pwa. AutoUpdate is set
 * in vite.config.ts — when a new SW is available, this calls update() and the
 * next navigation picks up the new assets.
 *
 * In DEVELOPMENT the PWA is disabled (vite.config `devOptions.enabled = false`),
 * but a service worker left over from a previous production build or
 * `vite preview` on the same origin (e.g. localhost:3002) keeps intercepting
 * requests and serving STALE, precached app bundles. That masks source changes
 * and produces confusing "ghost" state — a page that never reflects a
 * successful mutation, routes that 404 after being added, etc. So in dev we
 * proactively tear down any existing SW + caches and skip registration, which
 * guarantees the browser always loads fresh assets from the Vite dev server.
 */
export function registerServiceWorker(): void {
  if (typeof window === 'undefined') return;

  if (import.meta.env.DEV) {
    if ('serviceWorker' in navigator) {
      void navigator.serviceWorker.getRegistrations().then((regs) => {
        for (const reg of regs) void reg.unregister();
      });
    }
    if (typeof caches !== 'undefined') {
      void caches.keys().then((keys) => {
        for (const key of keys) void caches.delete(key);
      });
    }
    return;
  }

  const updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      // New SW is waiting. Auto-apply on next navigation.
      void updateSW(true);
    },
    onOfflineReady() {
      // First install — the app shell is now cached and usable offline.
    },
    onRegisterError(err) {
      console.warn('[pwa] service worker registration failed', err);
    },
  });
}
